import React, { useState, useEffect, useMemo } from "react";
import {
  ListFilter,
  Search,
  Plus,
  CheckCircle2,
  Circle,
  FileCheck2,
  FileText,
  Sliders,
  Trash2,
  Edit2,
  User,
  X,
  HelpCircle,
  MessageSquare,
  Calendar,
  Layers,
  Briefcase,
  FileSpreadsheet,
  ShieldCheck,
  Download,
  Lock,
} from "lucide-react";
import {
  Opportunity,
  Requirement,
  RequiredForm,
  FormatRule,
  RequirementCategory,
  SmeTask,
  ProposalMilestone,
  ProposalAddendum,
  UserProfile,
  DraftSection,
  StrategicAssessment,
} from "../../types";
import {
  subscribeToRequirements,
  subscribeToRequiredForms,
  subscribeToFormatRules,
  createRequirement,
  updateRequirement,
  toggleRequirementConfirmed,
  assignRequirement,
  deleteRequirement,
  bulkUpdateRequirements,
  bulkDeleteRequirements,
  assignRequiredForm,
  createRequiredForm,
  updateRequiredForm,
  deleteRequiredForm,
  createFormatRule,
  updateFormatRule,
  deleteFormatRule,
} from "../../services/requirementsService";
import { subscribeToMilestones } from "../../services/milestonesService";
import { subscribeToAddenda } from "../../services/addendaService";
import { subscribeToOpportunitySmeTasks } from "../../services/smeTasksService";
import { subscribeToDraftSections } from "../../services/draftsService";
import { subscribeToLatestAssessment } from "../../services/assessmentService";
import { auditPlanReadiness } from "../../services/planReadinessService";
import {
  calculateTeamWorkloads,
  buildProposalSectionGroups,
} from "../../services/teamAllocationService";
import { useStaffDirectory } from "../../services/staffService";
import { ExpandableCard } from "../common/ExpandableCard";
import { Modal } from "../common/Modal";
import { AskSmeModal } from "./AskSmeModal";
import { MilestonesTimeline } from "./MilestonesTimeline";
import { AddendaLog } from "./AddendaLog";
import { CategoryRollup } from "./CategoryRollup";
import { BulkActionBar } from "./BulkActionBar";
import { BulkAssignModal } from "./BulkAssignModal";
import { FormsChecklist } from "./FormsChecklist";
import { FormatRulesList } from "./FormatRulesList";
import { TeamWorkloadRollup } from "./TeamWorkloadRollup";
import { SectionAllocationMatrix } from "./SectionAllocationMatrix";
import { ExecutiveBriefView } from "./ExecutiveBriefView";
import { PlanReadinessModal } from "./PlanReadinessModal";
import { ExportPlanningModal } from "./ExportPlanningModal";

export type PlanSubTab =
  "schedule" | "addenda" | "requirements" | "sections" | "forms" | "rules" | "brief";

export interface RequirementsTabProps {
  opportunity: Opportunity;
  user?: UserProfile | null;
  initialSubTab?: PlanSubTab;
}

export const RequirementsTab: React.FC<RequirementsTabProps> = ({
  opportunity,
  user,
  initialSubTab = "requirements",
}) => {
  const [subTab, setSubTab] = useState<PlanSubTab>(initialSubTab);
  const [milestones, setMilestones] = useState<ProposalMilestone[]>([]);
  const [addenda, setAddenda] = useState<ProposalAddendum[]>([]);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [forms, setForms] = useState<RequiredForm[]>([]);
  const [rules, setRules] = useState<FormatRule[]>([]);
  const [draftSections, setDraftSections] = useState<DraftSection[]>([]);
  const [assessment, setAssessment] = useState<StrategicAssessment | null>(null);

  // Readiness & Export Modals
  const [isReadinessModalOpen, setIsReadinessModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Section Ownership filtering state
  const [selectedMemberEmail, setSelectedMemberEmail] = useState<string | null>(null);
  const [isUnassignedFiltered, setIsUnassignedFiltered] = useState(false);

  // Filtering & Search for Requirements
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [mandatoryFilter, setMandatoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [addendumFilter, setAddendumFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Selection & Bulk Operations State
  const [selectedReqIds, setSelectedReqIds] = useState<Set<string>>(new Set());
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Modals for Adding Manual Items
  const [isAddReqModalOpen, setIsAddReqModalOpen] = useState(false);
  const [editingReq, setEditingReq] = useState<Requirement | null>(null);
  const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<RequiredForm | null>(null);
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FormatRule | null>(null);

  // New Requirement Form State
  const [newReqTitle, setNewReqTitle] = useState("");
  const [newReqPrompt, setNewReqPrompt] = useState("");
  const [newReqSection, setNewReqSection] = useState("");
  const [newReqCategory, setNewReqCategory] = useState<RequirementCategory>("general");
  const [newReqMandatory, setNewReqMandatory] = useState(true);
  const [newReqPage, setNewReqPage] = useState("");

  // New Form State
  const [newFormName, setNewFormName] = useState("");
  const [newFormDesc, setNewFormDesc] = useState("");
  const [newFormMandatory, setNewFormMandatory] = useState(true);
  const [newFormPage, setNewFormPage] = useState("");

  // New Rule State
  const [newRuleType, setNewRuleType] = useState<FormatRule["ruleType"]>("page_limit");
  const [newRuleReq, setNewRuleReq] = useState("");
  const [newRulePage, setNewRulePage] = useState("");

  const [smeTasks, setSmeTasks] = useState<SmeTask[]>([]);
  const [activeSmeReq, setActiveSmeReq] = useState<Requirement | null>(null);

  const { staff } = useStaffDirectory();

  // Subscriptions
  useEffect(() => {
    const unsubReqs = subscribeToRequirements(opportunity.id, (newReqs) => {
      setRequirements(newReqs);
      setSelectedReqIds((prev) => {
        const validIds = new Set(newReqs.map((r) => r.id));
        const next = new Set<string>();
        prev.forEach((id) => {
          if (validIds.has(id)) next.add(id);
        });
        return next.size === prev.size ? prev : next;
      });
    });
    const unsubForms = subscribeToRequiredForms(opportunity.id, setForms);
    const unsubRules = subscribeToFormatRules(opportunity.id, setRules);
    const unsubSme = subscribeToOpportunitySmeTasks(opportunity.id, setSmeTasks);
    const unsubMilestones = subscribeToMilestones(opportunity.id, setMilestones);
    const unsubAddenda = subscribeToAddenda(opportunity.id, setAddenda);
    const unsubDrafts = subscribeToDraftSections(opportunity.id, setDraftSections);
    const unsubAssessment = subscribeToLatestAssessment(opportunity.id, setAssessment);

    return () => {
      unsubReqs();
      unsubForms();
      unsubRules();
      unsubSme();
      unsubMilestones();
      unsubAddenda();
      unsubDrafts();
      unsubAssessment();
    };
  }, [opportunity.id]);

  // Derived Workload & Proposal Section Groups
  const workloadSummary = useMemo(() => {
    return calculateTeamWorkloads(staff, draftSections, requirements, forms);
  }, [staff, draftSections, requirements, forms]);

  const sectionGroups = useMemo(() => {
    return buildProposalSectionGroups(draftSections, requirements);
  }, [draftSections, requirements]);

  const assignedSectionsCount = useMemo(() => {
    return sectionGroups.filter((g) => g.assignedTo || g.assignedToEmail).length;
  }, [sectionGroups]);

  // Plan Readiness Audit Report
  const readinessReport = useMemo(() => {
    return auditPlanReadiness({
      opportunity,
      requirements,
      milestones,
      addenda,
      forms,
      formatRules: rules,
      sectionGroups,
    });
  }, [opportunity, requirements, milestones, addenda, forms, rules, sectionGroups]);

  // Filtered Requirements
  const filteredRequirements = useMemo(() => {
    return requirements.filter((req) => {
      if (categoryFilter !== "all" && req.category !== categoryFilter) return false;
      if (mandatoryFilter === "mandatory" && !req.mandatory) return false;
      if (mandatoryFilter === "optional" && req.mandatory) return false;
      if (statusFilter === "confirmed" && !req.confirmed) return false;
      if (statusFilter === "unconfirmed" && req.confirmed) return false;
      if (assigneeFilter === "unassigned" && req.assignedToEmail) return false;
      if (
        assigneeFilter !== "all" &&
        assigneeFilter !== "unassigned" &&
        req.assignedToEmail !== assigneeFilter
      ) {
        return false;
      }
      if (addendumFilter === "none" && req.addendumTags && req.addendumTags.length > 0)
        return false;
      if (
        addendumFilter !== "all" &&
        addendumFilter !== "none" &&
        (!req.addendumTags || !req.addendumTags.includes(addendumFilter))
      ) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = req.title.toLowerCase().includes(q);
        const matchesPrompt = req.prompt.toLowerCase().includes(q);
        const matchesSec = req.sectionRef?.toLowerCase().includes(q);
        if (!matchesTitle && !matchesPrompt && !matchesSec) return false;
      }
      return true;
    });
  }, [
    requirements,
    categoryFilter,
    mandatoryFilter,
    statusFilter,
    assigneeFilter,
    addendumFilter,
    searchQuery,
  ]);

  const openMilestonesCount = useMemo(
    () => milestones.filter((m) => m.status === "pending").length,
    [milestones],
  );
  const pendingAddendaAckCount = useMemo(
    () => addenda.filter((a) => a.acknowledgmentStatus === "pending_acknowledgment").length,
    [addenda],
  );
  const confirmedCount = requirements.filter((r) => r.confirmed).length;
  const formsCompletedCount = forms.filter(
    (f) => f.status === "attached" || f.status === "signed",
  ).length;

  const uniqueAssignees = useMemo(() => {
    const map = new Map<string, string>();
    requirements.forEach((r) => {
      if (r.assignedToEmail && r.assignedTo) {
        map.set(r.assignedToEmail, r.assignedTo);
      }
    });
    return Array.from(map.entries()).map(([email, displayName]) => ({ email, displayName }));
  }, [requirements]);

  const uniqueAddendaTags = useMemo(() => {
    const set = new Set<string>();
    requirements.forEach((r) => {
      r.addendumTags?.forEach((tag) => set.add(tag));
    });
    return Array.from(set).sort();
  }, [requirements]);

  const isAllSelected = useMemo(
    () =>
      filteredRequirements.length > 0 &&
      filteredRequirements.every((r) => selectedReqIds.has(r.id)),
    [filteredRequirements, selectedReqIds],
  );

  const handleToggleSelectAll = () => {
    const filteredIds = filteredRequirements.map((r) => r.id);
    setSelectedReqIds((prev) => {
      const next = new Set(prev);
      if (isAllSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleToggleSelectReq = (id: string) => {
    setSelectedReqIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkAssign = async (staffMember: { displayName: string; email: string } | null) => {
    const ids = Array.from(selectedReqIds);
    await bulkUpdateRequirements(opportunity.id, ids, {
      assignedTo: staffMember ? staffMember.displayName : null,
      assignedToEmail: staffMember ? staffMember.email : null,
    });
    setSelectedReqIds(new Set());
  };

  const handleBulkCategory = async (cat: RequirementCategory) => {
    const ids = Array.from(selectedReqIds);
    await bulkUpdateRequirements(opportunity.id, ids, { category: cat });
  };

  const handleBulkToggleMandatory = async (mandatory: boolean) => {
    const ids = Array.from(selectedReqIds);
    await bulkUpdateRequirements(opportunity.id, ids, { mandatory });
  };

  const handleBulkConfirm = async (confirmed: boolean) => {
    const ids = Array.from(selectedReqIds);
    await bulkUpdateRequirements(opportunity.id, ids, { confirmed });
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedReqIds);
    setIsBulkDeleting(true);
    try {
      await bulkDeleteRequirements(opportunity.id, ids, addenda);
      setSelectedReqIds(new Set());
      setIsBulkDeleteModalOpen(false);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleCreateRequirement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReqTitle.trim() || !newReqPrompt.trim()) return;

    if (editingReq) {
      await updateRequirement(opportunity.id, editingReq.id, {
        title: newReqTitle.trim(),
        prompt: newReqPrompt.trim(),
        sectionRef: newReqSection.trim() || undefined,
        category: newReqCategory,
        mandatory: newReqMandatory,
        sourcePage: newReqPage.trim() || undefined,
      });
      setEditingReq(null);
    } else {
      await createRequirement(opportunity.id, {
        title: newReqTitle.trim(),
        prompt: newReqPrompt.trim(),
        sectionRef: newReqSection.trim() || undefined,
        category: newReqCategory,
        mandatory: newReqMandatory,
        sourcePage: newReqPage.trim() || undefined,
        status: "unanswered",
        source: "manual",
        humanEdited: true,
        confirmed: false,
      });
    }

    setNewReqTitle("");
    setNewReqPrompt("");
    setNewReqSection("");
    setNewReqCategory("general");
    setNewReqMandatory(true);
    setNewReqPage("");
    setIsAddReqModalOpen(false);
  };

  const handleOpenEdit = (req: Requirement) => {
    setEditingReq(req);
    setNewReqTitle(req.title);
    setNewReqPrompt(req.prompt);
    setNewReqSection(req.sectionRef || "");
    setNewReqCategory(req.category);
    setNewReqMandatory(req.mandatory);
    setNewReqPage(req.sourcePage ? String(req.sourcePage) : "");
    setIsAddReqModalOpen(true);
  };

  const handleCreateForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFormName.trim()) return;

    if (editingForm) {
      await updateRequiredForm(opportunity.id, editingForm.id, {
        formName: newFormName.trim(),
        description: newFormDesc.trim(),
        mandatory: newFormMandatory,
        sourcePage: newFormPage.trim() || undefined,
      });
      setEditingForm(null);
    } else {
      await createRequiredForm(opportunity.id, {
        formName: newFormName.trim(),
        description: newFormDesc.trim(),
        mandatory: newFormMandatory,
        sourcePage: newFormPage.trim() || undefined,
        status: "not_started",
        source: "manual",
        humanEdited: true,
        confirmed: false,
      });
    }

    setNewFormName("");
    setNewFormDesc("");
    setNewFormMandatory(true);
    setNewFormPage("");
    setIsAddFormModalOpen(false);
  };

  const handleOpenEditForm = (form: RequiredForm) => {
    setEditingForm(form);
    setNewFormName(form.formName);
    setNewFormDesc(form.description);
    setNewFormMandatory(form.mandatory);
    setNewFormPage(form.sourcePage ? String(form.sourcePage) : "");
    setIsAddFormModalOpen(true);
  };

  const handleCreateRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRuleReq.trim()) return;

    if (editingRule) {
      await updateFormatRule(opportunity.id, editingRule.id, {
        ruleType: newRuleType,
        requirement: newRuleReq.trim(),
        sourcePage: newRulePage.trim() || undefined,
      });
      setEditingRule(null);
    } else {
      await createFormatRule(opportunity.id, {
        ruleType: newRuleType,
        requirement: newRuleReq.trim(),
        sourcePage: newRulePage.trim() || undefined,
        isCompliant: true,
        source: "manual",
        humanEdited: true,
        confirmed: false,
      });
    }

    setNewRuleReq("");
    setNewRulePage("");
    setIsAddRuleModalOpen(false);
  };

  const handleOpenEditRule = (rule: FormatRule) => {
    setEditingRule(rule);
    setNewRuleType(rule.ruleType);
    setNewRuleReq(rule.requirement);
    setNewRulePage(rule.sourcePage ? String(rule.sourcePage) : "");
    setIsAddRuleModalOpen(true);
  };

  const getCategoryColor = (cat: RequirementCategory) => {
    switch (cat) {
      case "technical":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300";
      case "qualifications":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300";
      case "pricing":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300";
      case "compliance":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300";
      case "management":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300";
      default:
        return "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300";
    }
  };

  return (
    <div className="space-y-6">
      {/* Sub-navigation Pills & Quick Counters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 pb-3 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSubTab("schedule")}
            className={`btn-animate flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
              subTab === "schedule"
                ? "bg-[#2ba5b5] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            <Calendar className="h-4 w-4" />
            <span>Response Schedule</span>
            {openMilestonesCount > 0 && (
              <span
                className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                  subTab === "schedule"
                    ? "bg-white/20 text-white"
                    : "bg-slate-200 dark:bg-slate-700"
                }`}
              >
                {openMilestonesCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setSubTab("addenda")}
            className={`btn-animate flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
              subTab === "addenda"
                ? "bg-[#2ba5b5] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            <Layers className="h-4 w-4" />
            <span>RFP Addenda</span>
            <span
              className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                subTab === "addenda" ? "bg-white/20 text-white" : "bg-slate-200 dark:bg-slate-700"
              }`}
            >
              {addenda.length}
            </span>
            {pendingAddendaAckCount > 0 && (
              <span
                className="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
                title={`${pendingAddendaAckCount} addenda pending signed form`}
              />
            )}
          </button>

          <button
            type="button"
            onClick={() => setSubTab("requirements")}
            className={`btn-animate flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
              subTab === "requirements"
                ? "bg-[#2ba5b5] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            <FileText className="h-4 w-4" />
            <span>Requirements Matrix</span>
            <span
              className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                subTab === "requirements"
                  ? "bg-white/20 text-white"
                  : "bg-slate-200 dark:bg-slate-700"
              }`}
            >
              {confirmedCount}/{requirements.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setSubTab("sections")}
            className={`btn-animate flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
              subTab === "sections"
                ? "bg-[#2ba5b5] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            <Briefcase className="h-4 w-4" />
            <span>Section Ownership</span>
            <span
              className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                subTab === "sections" ? "bg-white/20 text-white" : "bg-slate-200 dark:bg-slate-700"
              }`}
            >
              {assignedSectionsCount}/{sectionGroups.length}
            </span>
            {workloadSummary.unassigned.sectionsCount > 0 && (
              <span
                className="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
                title={`${workloadSummary.unassigned.sectionsCount} sections unassigned`}
              />
            )}
          </button>

          <button
            type="button"
            onClick={() => setSubTab("forms")}
            className={`btn-animate flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
              subTab === "forms"
                ? "bg-[#2ba5b5] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            <FileCheck2 className="h-4 w-4" />
            <span>Required Forms</span>
            <span
              className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                subTab === "forms" ? "bg-white/20 text-white" : "bg-slate-200 dark:bg-slate-700"
              }`}
            >
              {formsCompletedCount}/{forms.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setSubTab("rules")}
            className={`btn-animate flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
              subTab === "rules"
                ? "bg-[#2ba5b5] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            <Sliders className="h-4 w-4" />
            <span>Format & Submission Rules</span>
            <span
              className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                subTab === "rules" ? "bg-white/20 text-white" : "bg-slate-200 dark:bg-slate-700"
              }`}
            >
              {rules.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setSubTab("brief")}
            className={`btn-animate flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
              subTab === "brief"
                ? "bg-[#2ba5b5] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            <FileSpreadsheet className="h-4 w-4" />
            <span>Executive Brief</span>
            {opportunity.planFrozen ? <Lock className="h-3 w-3 text-emerald-500" /> : null}
          </button>
        </div>

        {/* Global Planning Actions & Add Actions */}
        <div className="flex items-center gap-2">
          {/* Readiness Gate Button */}
          <button
            type="button"
            onClick={() => setIsReadinessModalOpen(true)}
            className="btn-animate flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title="Open Plan Readiness Gate"
          >
            {opportunity.planFrozen ? (
              <>
                <Lock className="h-3.5 w-3.5 text-emerald-500" />
                <span className="text-emerald-700 dark:text-emerald-300">Plan Frozen</span>
              </>
            ) : (
              <>
                <ShieldCheck className="h-3.5 w-3.5 text-[#2ba5b5]" />
                <span>Readiness: {readinessReport.overallScore}%</span>
              </>
            )}
          </button>

          {/* Export Button */}
          <button
            type="button"
            onClick={() => setIsExportModalOpen(true)}
            className="btn-animate flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title="Export Planning Package"
          >
            <Download className="h-3.5 w-3.5" />
            <span>Export</span>
          </button>

          {subTab === "requirements" && (
            <button
              type="button"
              onClick={() => {
                setEditingReq(null);
                setNewReqTitle("");
                setNewReqPrompt("");
                setNewReqSection("");
                setNewReqCategory("general");
                setNewReqMandatory(true);
                setNewReqPage("");
                setIsAddReqModalOpen(true);
              }}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97]"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Add Requirement</span>
            </button>
          )}

          {subTab === "forms" && (
            <button
              type="button"
              onClick={() => setIsAddFormModalOpen(true)}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97]"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Add Form</span>
            </button>
          )}

          {subTab === "rules" && (
            <button
              type="button"
              onClick={() => setIsAddRuleModalOpen(true)}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97]"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Add Format Rule</span>
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* SUB-TAB 0: RESPONSE SCHEDULE & MILESTONES */}
      {/* ========================================================================= */}
      {subTab === "schedule" && (
        <MilestonesTimeline opportunity={opportunity} user={user || null} />
      )}

      {/* ========================================================================= */}
      {/* SUB-TAB 0B: RFP ADDENDA & SCOPE CHANGES */}
      {/* ========================================================================= */}
      {subTab === "addenda" && (
        <AddendaLog opportunity={opportunity} requirements={requirements} user={user} />
      )}

      {/* ========================================================================= */}
      {/* SUB-TAB 1: REQUIREMENTS MATRIX */}
      {/* ========================================================================= */}
      {subTab === "requirements" && (
        <div className="space-y-4">
          {/* Category Rollup Cards */}
          <CategoryRollup
            requirements={requirements}
            activeCategory={categoryFilter}
            onSelectCategory={setCategoryFilter}
          />

          {/* Filters Bar */}
          <div className="flex flex-col gap-3 rounded-xl border border-slate-200/80 bg-white p-3.5 shadow-xs sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-1 flex-wrap items-center gap-2 text-xs">
              {/* Select All */}
              <label className="flex items-center gap-1.5 cursor-pointer text-slate-700 dark:text-slate-300 mr-1 select-none">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleToggleSelectAll}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5] dark:border-slate-700 dark:bg-slate-800"
                  aria-label="Select all filtered requirements"
                />
                <span className="font-semibold text-slate-600 dark:text-slate-300">All</span>
              </label>

              <div className="flex items-center gap-1 text-slate-400">
                <ListFilter className="h-3.5 w-3.5" />
                <span className="font-medium">Filter:</span>
              </div>

              {/* Category */}
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                aria-label="Filter by category"
              >
                <option value="all">All Categories</option>
                <option value="technical">Technical</option>
                <option value="qualifications">Qualifications</option>
                <option value="pricing">Pricing</option>
                <option value="management">Management</option>
                <option value="compliance">Compliance</option>
                <option value="general">General</option>
              </select>

              {/* Mandatory */}
              <select
                value={mandatoryFilter}
                onChange={(e) => setMandatoryFilter(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                aria-label="Filter by priority"
              >
                <option value="all">All Priority</option>
                <option value="mandatory">Mandatory Only</option>
                <option value="optional">Optional Only</option>
              </select>

              {/* Status */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                aria-label="Filter by confirmation"
              >
                <option value="all">All Confirmation</option>
                <option value="confirmed">Confirmed</option>
                <option value="unconfirmed">Unconfirmed</option>
              </select>

              {/* Assignee Filter */}
              <select
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                aria-label="Filter by assignee"
              >
                <option value="all">All Assignees</option>
                <option value="unassigned">Unassigned</option>
                {uniqueAssignees.map((a) => (
                  <option key={a.email} value={a.email}>
                    {a.displayName}
                  </option>
                ))}
              </select>

              {/* Addenda Filter */}
              {uniqueAddendaTags.length > 0 && (
                <select
                  value={addendumFilter}
                  onChange={(e) => setAddendumFilter(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  aria-label="Filter by addendum"
                >
                  <option value="all">All Addenda</option>
                  <option value="none">No Addenda</option>
                  {uniqueAddendaTags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Keyword Search */}
            <div className="relative sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search prompt or title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>

          {/* Requirements List */}
          {filteredRequirements.length > 0 ? (
            <div className="space-y-3">
              {filteredRequirements.map((req) => {
                const headerTitle = req.sectionRef ? `${req.sectionRef} ${req.title}` : req.title;
                const relatedSmeTask = smeTasks.find((t) => t.requirementId === req.id);

                return (
                  <ExpandableCard
                    key={req.id}
                    title={headerTitle}
                    badge={req.category.toUpperCase()}
                    badgeColor={getCategoryColor(req.category)}
                    summary={
                      <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
                        {req.mandatory ? (
                          <span className="rounded-md bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">
                            Mandatory
                          </span>
                        ) : (
                          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                            Optional
                          </span>
                        )}

                        <span className="rounded-md bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-[#2ba5b5] dark:bg-teal-950/40 dark:text-teal-300">
                          {req.source === "ai" ? "AI Extracted" : "Manual"}
                        </span>

                        {req.sourcePage && (
                          <span className="text-slate-400 text-[11px]">
                            Source: Page {req.sourcePage}
                          </span>
                        )}

                        {req.assignedTo && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            <User className="h-3 w-3 text-slate-400" />
                            <span>{req.assignedTo}</span>
                          </span>
                        )}

                        {req.addendumTags &&
                          req.addendumTags.length > 0 &&
                          req.addendumTags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800"
                            >
                              <Layers className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                              <span>{tag}</span>
                            </span>
                          ))}

                        {relatedSmeTask && (
                          <span
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                              relatedSmeTask.status === "open"
                                ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
                                : relatedSmeTask.status === "submitted"
                                  ? "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300"
                                  : relatedSmeTask.status === "accepted"
                                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                                    : "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300"
                            }`}
                          >
                            <HelpCircle className="h-3 w-3" />
                            <span>
                              {relatedSmeTask.status === "open"
                                ? `SME Pending (${relatedSmeTask.assigneeName})`
                                : relatedSmeTask.status === "submitted"
                                  ? `SME Answer Ready (${relatedSmeTask.assigneeName})`
                                  : relatedSmeTask.status === "accepted"
                                    ? "SME Answer Accepted"
                                    : "SME Question Returned"}
                            </span>
                          </span>
                        )}
                      </div>
                    }
                    headerActions={
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedReqIds.has(req.id)}
                          onChange={() => handleToggleSelectReq(req.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5] dark:border-slate-700 dark:bg-slate-800"
                          aria-label={`Select requirement ${req.title}`}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            toggleRequirementConfirmed(opportunity.id, req.id, req.confirmed)
                          }
                          title={req.confirmed ? "Mark as unconfirmed" : "Confirm requirement"}
                          className={`btn-animate inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold ${
                            req.confirmed
                              ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400"
                          }`}
                        >
                          {req.confirmed ? (
                            <>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              <span>Confirmed</span>
                            </>
                          ) : (
                            <>
                              <Circle className="h-3.5 w-3.5" />
                              <span>Confirm</span>
                            </>
                          )}
                        </button>
                      </div>
                    }
                    details={
                      <div className="space-y-4 text-xs">
                        <div>
                          <h4 className="font-semibold text-slate-700 dark:text-slate-300">
                            RFP Specification / Question:
                          </h4>
                          <p className="mt-1 whitespace-pre-wrap text-slate-600 dark:text-slate-300">
                            {req.prompt}
                          </p>
                        </div>

                        {/* Routed SME Task Panel */}
                        {relatedSmeTask && (
                          <div className="rounded-lg border border-teal-200/80 bg-teal-50/40 p-3 dark:border-teal-900/40 dark:bg-teal-950/20">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 font-semibold text-teal-800 dark:text-teal-300">
                                <HelpCircle className="h-3.5 w-3.5" />
                                <span>SME Question ({relatedSmeTask.assigneeName})</span>
                              </div>
                              <span className="text-[11px] font-medium uppercase text-teal-700 dark:text-teal-300">
                                Status: {relatedSmeTask.status}
                              </span>
                            </div>
                            <p className="mt-1 text-xs italic text-slate-700 dark:text-slate-300">
                              &ldquo;{relatedSmeTask.question}&rdquo;
                            </p>
                            {relatedSmeTask.response && (
                              <div className="mt-2.5 rounded-md border border-slate-200 bg-white p-2.5 text-xs text-slate-800 shadow-2xs dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                  <MessageSquare className="h-3 w-3 text-blue-500" />
                                  <span>SME Response:</span>
                                </div>
                                <p className="mt-1 whitespace-pre-wrap">
                                  {relatedSmeTask.response}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Controls: Owner assignment & actions */}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-slate-100 pt-3 dark:border-slate-800">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500 font-medium">Assignee:</span>
                            <select
                              value={req.assignedToEmail || ""}
                              onChange={(e) => {
                                const selectedEmail = e.target.value;
                                const staffMember =
                                  staff.find((s) => s.email === selectedEmail) || null;
                                assignRequirement(opportunity.id, req.id, staffMember);
                              }}
                              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              <option value="">Unassigned</option>
                              {staff.map((s) => (
                                <option key={s.id} value={s.email}>
                                  {s.displayName} ({s.department || "Staff"})
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setActiveSmeReq(req)}
                              className="btn-animate inline-flex items-center gap-1 rounded-md border border-[#2ba5b5] bg-teal-50 px-2.5 py-1 text-xs font-semibold text-[#0d7482] hover:bg-teal-100 dark:bg-teal-950/40 dark:text-teal-300"
                            >
                              <HelpCircle className="h-3 w-3" />
                              <span>{relatedSmeTask ? "Ask Follow-Up" : "Ask an SME"}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(req)}
                              className="btn-animate inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              <Edit2 className="h-3 w-3 text-slate-400" />
                              <span>Edit</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteRequirement(opportunity.id, req.id)}
                              className="btn-animate rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40"
                              title="Delete requirement"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    }
                  />
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center dark:border-slate-800">
              <FileText className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
              <p className="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                No requirements match current filters
              </p>
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                Run RFP extraction in the Intake tab or add a requirement manually.
              </p>
            </div>
          )}

          {/* Bulk Action Bar */}
          <BulkActionBar
            selectedCount={selectedReqIds.size}
            onClearSelection={() => setSelectedReqIds(new Set())}
            onBulkAssign={() => setIsBulkAssignOpen(true)}
            onBulkCategory={handleBulkCategory}
            onBulkToggleMandatory={handleBulkToggleMandatory}
            onBulkConfirm={handleBulkConfirm}
            onBulkDelete={() => setIsBulkDeleteModalOpen(true)}
          />

          {/* Bulk Assign Modal */}
          <BulkAssignModal
            isOpen={isBulkAssignOpen}
            onClose={() => setIsBulkAssignOpen(false)}
            count={selectedReqIds.size}
            onAssign={handleBulkAssign}
          />

          {/* Bulk Delete Confirmation Modal */}
          <Modal
            isOpen={isBulkDeleteModalOpen}
            onClose={() => setIsBulkDeleteModalOpen(false)}
            title="Delete Selected Requirements"
            maxWidth="sm"
          >
            <div className="space-y-4">
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Are you sure you want to permanently delete{" "}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {selectedReqIds.size}
                </span>{" "}
                selected requirement{selectedReqIds.size === 1 ? "" : "s"}? Any linked addenda will
                also be unlinked.
              </p>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsBulkDeleteModalOpen(false)}
                  className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isBulkDeleting}
                  onClick={handleConfirmBulkDelete}
                  className="btn-animate rounded-lg bg-rose-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-rose-700 disabled:opacity-50"
                >
                  {isBulkDeleting ? "Deleting..." : "Delete Requirements"}
                </button>
              </div>
            </div>
          </Modal>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-TAB: BID SECTION OWNERSHIP & WORKLOAD ALLOCATION */}
      {/* ========================================================================= */}
      {subTab === "sections" && (
        <div className="space-y-6">
          <TeamWorkloadRollup
            summary={workloadSummary}
            selectedMemberEmail={selectedMemberEmail}
            onSelectMember={setSelectedMemberEmail}
            onFilterUnassigned={() => setIsUnassignedFiltered((prev) => !prev)}
            isUnassignedFiltered={isUnassignedFiltered}
          />
          <SectionAllocationMatrix
            opportunity={opportunity}
            user={user || null}
            sectionGroups={sectionGroups}
            requirements={requirements}
            draftSections={draftSections}
            selectedMemberEmail={selectedMemberEmail}
            onClearMemberFilter={() => setSelectedMemberEmail(null)}
            isUnassignedFiltered={isUnassignedFiltered}
            onClearUnassignedFilter={() => setIsUnassignedFiltered(false)}
          />
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUB-TAB: REQUIRED FORMS CHECKLIST */}
      {/* ========================================================================= */}
      {subTab === "forms" && (
        <FormsChecklist
          opportunity={opportunity}
          forms={forms}
          onAddForm={() => {
            setEditingForm(null);
            setNewFormName("");
            setNewFormDesc("");
            setNewFormMandatory(true);
            setNewFormPage("");
            setIsAddFormModalOpen(true);
          }}
          onEditForm={handleOpenEditForm}
          onDeleteForm={async (formId) => {
            await deleteRequiredForm(opportunity.id, formId);
          }}
          onToggleConfirmed={(form) => {
            updateRequiredForm(opportunity.id, form.id, { confirmed: !form.confirmed });
          }}
          onUpdateStatus={(formId, status) => {
            updateRequiredForm(opportunity.id, formId, { status });
          }}
          onAssignStaff={(formId, staffMember) => {
            assignRequiredForm(opportunity.id, formId, staffMember);
          }}
          onUpdateNotes={(formId, notes) => {
            updateRequiredForm(opportunity.id, formId, { notes });
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* SUB-TAB 3: FORMAT & SUBMISSION RULES */}
      {/* ========================================================================= */}
      {subTab === "rules" && (
        <FormatRulesList
          opportunity={opportunity}
          rules={rules}
          onAddRule={() => {
            setEditingRule(null);
            setNewRuleType("page_limit");
            setNewRuleReq("");
            setNewRulePage("");
            setIsAddRuleModalOpen(true);
          }}
          onEditRule={handleOpenEditRule}
          onDeleteRule={async (ruleId) => {
            await deleteFormatRule(opportunity.id, ruleId);
          }}
          onToggleCompliance={(rule) => {
            updateFormatRule(opportunity.id, rule.id, { isCompliant: !rule.isCompliant });
          }}
          onToggleConfirmed={(rule) => {
            updateFormatRule(opportunity.id, rule.id, { confirmed: !rule.confirmed });
          }}
          onUpdateNotes={(ruleId, notes) => {
            updateFormatRule(opportunity.id, ruleId, { notes });
          }}
        />
      )}

      {/* ========================================================================= */}
      {/* SUB-TAB: EXECUTIVE OPPORTUNITY BRIEF & PLAN SUMMARY */}
      {/* ========================================================================= */}
      {subTab === "brief" && (
        <ExecutiveBriefView
          opportunity={opportunity}
          assessment={assessment}
          requirements={requirements}
          milestones={milestones}
          addenda={addenda}
          forms={forms}
          formatRules={rules}
          sectionGroups={sectionGroups}
          readinessReport={readinessReport}
          user={user}
          onOpenReadinessModal={() => setIsReadinessModalOpen(true)}
          onOpenExportModal={() => setIsExportModalOpen(true)}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL: ADD / EDIT REQUIREMENT */}
      {/* ========================================================================= */}
      {isAddReqModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 dark:border-slate-800">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {editingReq ? "Edit Requirement" : "Add Manual Requirement"}
              </h3>
              <button
                type="button"
                onClick={() => setIsAddReqModalOpen(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateRequirement} className="mt-4 space-y-4 text-xs">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-medium text-slate-700 dark:text-slate-300">
                    Section Ref
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 3.2.1"
                    value={newReqSection}
                    onChange={(e) => setNewReqSection(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block font-medium text-slate-700 dark:text-slate-300">
                    Title *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Translation Quality Assurance"
                    value={newReqTitle}
                    onChange={(e) => setNewReqTitle(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block font-medium text-slate-700 dark:text-slate-300">
                  Full Prompt / RFP Requirement *
                </label>
                <textarea
                  rows={4}
                  required
                  placeholder="Paste or write the full RFP question or specification..."
                  value={newReqPrompt}
                  onChange={(e) => setNewReqPrompt(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-medium text-slate-700 dark:text-slate-300">
                    Category
                  </label>
                  <select
                    value={newReqCategory}
                    onChange={(e) => setNewReqCategory(e.target.value as RequirementCategory)}
                    className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="technical">Technical</option>
                    <option value="qualifications">Qualifications</option>
                    <option value="pricing">Pricing</option>
                    <option value="management">Management</option>
                    <option value="compliance">Compliance</option>
                    <option value="general">General</option>
                  </select>
                </div>

                <div>
                  <label className="block font-medium text-slate-700 dark:text-slate-300">
                    Source Page
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 14"
                    value={newReqPage}
                    onChange={(e) => setNewReqPage(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                <div className="flex items-center pt-5">
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={newReqMandatory}
                      onChange={(e) => setNewReqMandatory(e.target.checked)}
                      className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5]"
                    />
                    <span>Mandatory</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddReqModalOpen(false)}
                  className="rounded-lg border border-slate-200 px-4 py-2 font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-2 font-semibold text-white shadow-sm hover:bg-[#238a97]"
                >
                  {editingReq ? "Save Changes" : "Create Requirement"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: ADD FORM */}
      {/* ========================================================================= */}
      {isAddFormModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 dark:border-slate-800">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {editingForm ? "Edit Required Form" : "Add Required Form"}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setIsAddFormModalOpen(false);
                  setEditingForm(null);
                }}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateForm} className="mt-4 space-y-4 text-xs">
              <div>
                <label className="block font-medium text-slate-700 dark:text-slate-300">
                  Form Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Non-Collusion Affidavit"
                  value={newFormName}
                  onChange={(e) => setNewFormName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-700 dark:text-slate-300">
                  Description / Instructions
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Must be signed by executive and notarized..."
                  value={newFormDesc}
                  onChange={(e) => setNewFormDesc(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium text-slate-700 dark:text-slate-300">
                    Source Page
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Attachment B"
                    value={newFormPage}
                    onChange={(e) => setNewFormPage(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                <div className="flex items-center pt-5">
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={newFormMandatory}
                      onChange={(e) => setNewFormMandatory(e.target.checked)}
                      className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5]"
                    />
                    <span>Mandatory Form</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddFormModalOpen(false);
                    setEditingForm(null);
                  }}
                  className="rounded-lg border border-slate-200 px-4 py-2 font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-2 font-semibold text-white shadow-sm hover:bg-[#238a97]"
                >
                  {editingForm ? "Save Changes" : "Add Form"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: ADD FORMAT RULE */}
      {/* ========================================================================= */}
      {isAddRuleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 dark:border-slate-800">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {editingRule ? "Edit Format Rule" : "Add Format Rule"}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setIsAddRuleModalOpen(false);
                  setEditingRule(null);
                }}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateRule} className="mt-4 space-y-4 text-xs">
              <div>
                <label className="block font-medium text-slate-700 dark:text-slate-300">
                  Rule Type
                </label>
                <select
                  value={newRuleType}
                  onChange={(e) => setNewRuleType(e.target.value as FormatRule["ruleType"])}
                  className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                >
                  <option value="page_limit">Page Limit</option>
                  <option value="font">Font / Size</option>
                  <option value="margin">Margins / Spacing</option>
                  <option value="file_size">File Size Limit</option>
                  <option value="file_format">File Format</option>
                  <option value="submission_method">Submission Method</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block font-medium text-slate-700 dark:text-slate-300">
                  Requirement *
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="e.g. Max 25 pages, 1-inch margins, 11pt font..."
                  value={newRuleReq}
                  onChange={(e) => setNewRuleReq(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-700 dark:text-slate-300">
                  Source Page
                </label>
                <input
                  type="text"
                  placeholder="e.g. Page 4"
                  value={newRulePage}
                  onChange={(e) => setNewRulePage(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 p-2 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddRuleModalOpen(false);
                    setEditingRule(null);
                  }}
                  className="rounded-lg border border-slate-200 px-4 py-2 font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-2 font-semibold text-white shadow-sm hover:bg-[#238a97]"
                >
                  {editingRule ? "Save Changes" : "Add Format Rule"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ask an SME Modal */}
      {activeSmeReq && (
        <AskSmeModal
          isOpen={Boolean(activeSmeReq)}
          onClose={() => setActiveSmeReq(null)}
          opportunity={opportunity}
          requirement={activeSmeReq}
        />
      )}

      {/* Plan Readiness Gate & Baseline Freeze Modal */}
      <PlanReadinessModal
        isOpen={isReadinessModalOpen}
        onClose={() => setIsReadinessModalOpen(false)}
        opportunity={opportunity}
        report={readinessReport}
        user={user}
        onNavigateToSubTab={(tab) => setSubTab(tab)}
      />

      {/* Export Planning Package Modal */}
      <ExportPlanningModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        opportunity={opportunity}
        assessment={assessment}
        requirements={requirements}
        milestones={milestones}
        addenda={addenda}
        forms={forms}
        formatRules={rules}
        sectionGroups={sectionGroups}
      />
    </div>
  );
};
